import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals } from "../hex";
import { canTake } from "../land/territory";
import { nearestTo } from "../pick";
import { enemyWithin, findHex, unitAt } from "../run/board";
import { isMoveTick } from "../run/cadence";
import { isSeatUnit, seatAt, seatUnit, soldierSeats } from "../run/seats";
import { sideOf } from "../run/sides";
import type { WorkRouteRules } from "../types/rules";
import type { RunState, SeatId, Unit } from "../types/state";
import { routeUnit, walledAgainst } from "../units/routes";
import { replaceUnit } from "../units/unit";
import { findAt, isRevealed, prospectWait } from "./prospect";

/**
 * The soldier's work route (CC5, shipped off): a walk reads and takes the land on its road, then the
 * patch around its end.
 */

/** Whether a seat's soldier is at its work (CC5): sent by its seat to work a road and a patch, and not yet done. */
export function isWorking(state: RunState, unit: Unit): boolean {
  return unit.works !== undefined && isSeatUnit(state, unit.id);
}

/** CC5: a city's hex is no land for the soldier at its work: its yard is the King's recruits'. */
export function isCityHex(state: RunState, coord: AxialCoord): boolean {
  return findHex(state, coord)?.structure === "city";
}

/** CC5: a seat's own unit takes its seat's orders without the lock. */
export function ordersFreely(state: RunState, unit: Unit): boolean {
  return state.rules.soldier?.freeOrders === true && isSeatUnit(state, unit.id);
}

/**
 * How long the soldier at its work stands on a hex (CC5): the read while its seat has not read
 * the hex or a find it knows of lies there for its empty hands, the take while its King pays for
 * taking it (land of another owner it can take by standing), the longer of the two; 0 when the
 * hex holds no work for it, which a city's hex never does.
 */
function workTicksAt(state: RunState, unit: Unit, coord: AxialCoord): number {
  const hex = findHex(state, coord);
  if (!hex || hex.structure === "city") {
    return 0;
  }
  const { finds, bounty, captureTicks } = state.rules;
  const reads =
    finds !== null && (!isRevealed(state, unit.owner, coord) || findAt(state, coord) > 0)
      ? prospectWait(state, unit)
      : 0;
  const paid = bounty !== null && canTake(state, unit.owner, coord);
  return Math.max(reads, paid ? captureTicks : 0);
}

/** The soldier at its work has not stood out the work of the hex under it (CC5): it takes no step yet. */
export function holdsToWork(state: RunState, unit: Unit): boolean {
  return state.tick - unit.movedTick < workTicksAt(state, unit, unit.coord);
}

/**
 * The next hex of the soldier's patch (CC5): within `patchRadius` of the hex it was sent to, not
 * the one it stands on, holding work for it (never a city's), open to it (no foreign walls; nobody
 * on it), more than `enemyRange` from every enemy and on a road from it. The nearest first, ties
 * to map order.
 */
function nextPatchHex(
  state: RunState,
  unit: Unit,
  centre: AxialCoord,
  work: WorkRouteRules,
): AxialCoord | undefined {
  return nearestTo(
    state.hexes,
    unit.coord,
    (hex) =>
      !hexEquals(hex.coord, unit.coord) &&
      hexDistance(hex.coord, centre) <= work.patchRadius &&
      !hex.razed &&
      !walledAgainst(hex, sideOf(state.rules, unit.owner)) &&
      unitAt(state, hex.coord) === undefined &&
      workTicksAt(state, unit, hex.coord) !== 0 &&
      !enemyWithin(state, unit.owner, hex.coord, work.enemyRange) &&
      routeUnit(state, unit, hex.coord) !== undefined,
  )?.coord;
}

/**
 * Step 1f (CC5), every tick after the reading and before the march, seat by seat in id order: a
 * seat's soldier at its work. An enemy within `enemyRange` ends the work: the seat's walk goes on plain, a soldier in
 * its patch stands (the patch's own step included); a break has ended it already, with the walk,
 * in `breakUnits`, and a broken unit takes no order to start one. A find taken ends the walk too:
 * the soldier stands with its load, for its seat to say where it goes (a slow walk on to the
 * road's end is rarely the way to a market). On the march's tick, its road walked and the hex
 * under it worked, it is sent to the next hex of its patch, still on its seat's order
 * (`orderedBy` and `orderedTick` do not move), and once none is left its work is done.
 */
export function workPatch(state: RunState): RunState {
  if (!state.rules.soldier?.workRoute) {
    return state;
  }
  let next = state;
  for (const { id } of soldierSeats(state)) {
    next = workSeatPatch(next, id);
  }
  return next;
}

function workSeatPatch(state: RunState, seatId: SeatId): RunState {
  const work = state.rules.soldier?.workRoute;
  const unit = seatUnit(state, seatAt(state, seatId));
  if (!work || unit?.works === undefined) {
    return state;
  }
  const ends =
    unit.carry !== undefined || enemyWithin(state, unit.owner, unit.coord, work.enemyRange);
  if (!ends && (unit.destination !== undefined || !isMoveTick(state) || holdsToWork(state, unit))) {
    return state;
  }
  const next = ends ? undefined : nextPatchHex(state, unit, unit.works, work);
  const done = without(unit, "works");
  const standing = without(done, "destination");
  // The seat's own road ends where it sent the soldier; any other road is the patch's.
  const seatsRoad = unit.destination !== undefined && hexEquals(unit.destination, unit.works);
  const worked: Unit = next
    ? { ...unit, destination: next }
    : unit.carry || !seatsRoad
      ? standing
      : done;
  return replaceUnit(state, unit, worked);
}
